import {createSlice} from '@reduxjs/toolkit';
import { AddressState } from '../interfaces/address.interface';
import { StoreState } from '../../../store/store.interface';
import environment from '../../../common/environment';
import { AddressSuggestionMagentoResponse } from '../interfaces/address-suggestions.interface';
import { mapAddressSuggestionMagento } from '../mapping/address-suggestions.mapping';
import { ShopApi } from '../../../common/constants/shop-api';
import { getResponseData } from '../../../common/utils/api-utils';

/*----- CREATE STORE ------*/
declare interface AddressSlice {
  name: string;
  initialState: AddressState;
  reducers: any;
}

const slice: AddressSlice = {
  name: 'address',
  initialState: {
    suggestions: {},
  },
  reducers: {
    onSuggestionsSuccess: (state: AddressState, action: any) => {
      state.suggestions[action.payload.id] = {
        data: action.payload.suggestions,
        isLoaded: true,
        isLoading: false,
        isSuccess: true,
        isFailure: false,
        message: `Successfully loaded suggestions`,
      };
    },
    onSuggestionsFailure: (state: AddressState, action: any) => {
      state.suggestions[action.payload.id] = {
        data: action.payload,
        isLoaded: false,
        isLoading: false,
        isSuccess: false,
        isFailure: true,
        message: `Failed to load suggestions`,
      };
    },
    setSuggestionsLoading: (state: AddressState, action: any) => {
      state.suggestions[action.payload.id] = {
        data: action.payload,
        isLoaded: false,
        isLoading: true,
        isSuccess: false,
        isFailure: false,
        message: `Loading suggestions`,
      };
    },
    resetSuggestionsState: (state: AddressState, action: any) => {
      state.suggestions[action.payload] = {
        data: action.payload,
        isLoaded: false,
        isLoading: false,
        isSuccess: false,
        isFailure: false,
        isReset: true,
        message: `Reset suggestions`,
      };
    },
  },
};

export const addressSlice: any = createSlice(slice);

/*----- EXPORT ACTIONS ------*/
export const onSuggestionsSuccess: Function =
  addressSlice.actions.onSuggestionsSuccess;
export const onSuggestionsFailure: Function =
  addressSlice.actions.onSuggestionsFailure;
export const setSuggestionsLoading: Function =
  addressSlice.actions.setSuggestionsLoading;
export const resetSuggestionsState: Function =
  addressSlice.actions.resetSuggestionsState;

/*----- THUNKS ------*/
export const getAutoCompleteSuggestionsAsync = (
  id: string,
  searchString: string,
  secondaryExpansion?: string,
  oldComponent?: boolean
) => (dispatch: Function) => {
  dispatch(setSuggestionsLoading({id, searchString, secondaryExpansion}));

  getSuggestionsApiCall(searchString, secondaryExpansion, oldComponent)
    .then((suggestions) => dispatch(onSuggestionsSuccess({id, suggestions})))
    .catch(() => {
      const errorMessage = "Failed to retrieve suggestions for search string : '"+searchString+(secondaryExpansion ? " "+secondaryExpansion : "" )+"'.";
      dispatch(onSuggestionsFailure({id, errorMessage}))
    });
};

/*----- SELECTORS ------*/
export const getSuggestions = (id: string, state: StoreState) =>
  state.addressState.suggestions[id]?.data;
export const isSuggestionsLoading = (id: string, state: StoreState) =>
  state.addressState.suggestions[id]?.isLoading;
export const isSuggestionsLoaded = (id: string, state: StoreState) =>
  state.addressState.suggestions[id]?.isLoaded;
export const isSuggestionsSuccess = (id: string, state: StoreState) =>
  state.addressState.suggestions[id]?.isSuccess;
export const isSuggestionsFailure = (id: string, state: StoreState) =>
  state.addressState.suggestions[id]?.isFailure;
export const isSuggestionsReset = (id: string, state: StoreState) =>
  state.addressState.suggestions[id]?.isReset;

/*----- EXPORT DEFAULT ------*/
export const addressReducer = addressSlice.reducer;

/*----- HELPERS -----*/
async function getSuggestionsApiCall(
  searchString: string,
  secondaryExpansion?: string,
  _oldComponent?: boolean
) {
  return getAddressSuggestionsMagento(searchString, secondaryExpansion);
}

async function getAddressSuggestionsMagento(searchString, secondaryExpansion) {
  const url = new URL(`${environment().magentoUrl}${ShopApi.addressSuggestionMagento}`);
    const response: AddressSuggestionMagentoResponse = await getResponseData(
      new Request(url.toString(),{
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          search: searchString,
          selected: secondaryExpansion,
        }),
      })
    );
    return mapAddressSuggestionMagento(response);
}